(() => {
  class AgilitasLabTechnologies {
    constructor(section) {
      this.section = section;
      this.tabs = Array.from(section.querySelectorAll('[data-alt-tab]'));
      this.groups = Array.from(section.querySelectorAll('[data-alt-group]'));
      this.sliderElements = Array.from(section.querySelectorAll('[data-alt-slider]'));
      this.modal = section.querySelector('[data-alt-modal]');
      this.modalCode = section.querySelector('[data-alt-modal-code]');
      this.modalValue = section.querySelector('[data-alt-modal-value]');
      this.modalTitle = section.querySelector('[data-alt-modal-title]');
      this.modalDescription = section.querySelector('[data-alt-modal-description]');
      this.modalSubtext = section.querySelector('[data-alt-modal-subtext]');
      this.modalCloseButtons = Array.from(section.querySelectorAll('[data-alt-modal-close]'));
      this.modalTriggers = Array.from(section.querySelectorAll('[data-alt-modal-trigger]'));
      this.mobileMq = window.matchMedia('(max-width: 749px)');
      this.swipers = new WeakMap();
      this.retryTimer = null;
      this.initAttempt = 0;
      this.activeTrigger = null;
      this.modalAnimationTimer = null;
      this.resizeHandler = this.handleResize.bind(this);
      this.mediaChangeHandler = this.handleResize.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
      this.accordionAnimationDuration = 420;
      this.accordionAnimationEasing = 'cubic-bezier(0.22, 1, 0.36, 1)';

      this.bindTabs();
      this.bindAccordions();
      this.bindModal();
      this.waitForSetup();

      window.addEventListener('resize', this.resizeHandler);
      if (this.mobileMq.addEventListener) {
        this.mobileMq.addEventListener('change', this.mediaChangeHandler);
      } else {
        this.mobileMq.addListener(this.mediaChangeHandler);
      }
    }

    waitForSetup() {
      clearTimeout(this.retryTimer);

      if (typeof window.Swiper === 'function' || this.initAttempt > 20) {
        this.initAttempt = 0;
        this.setup();
        return;
      }

      this.initAttempt += 1;
      this.retryTimer = window.setTimeout(() => this.waitForSetup(), 150);
    }

    setup() {
      this.updateGroups();
      this.setupSliders();
    }

    bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const index = Number(tab.dataset.tabIndex || 0);
          this.activateTab(index);
        });
      });
    }

    bindAccordions() {
      this.groups.forEach((group) => {
        const items = Array.from(group.querySelectorAll('[data-alt-item]'));

        items.forEach((item) => {
          const summary = item.querySelector('[data-alt-summary]');
          if (!summary) return;

          summary.addEventListener('click', () => {
            if (!this.mobileMq.matches) return;

            const isOpen = item.classList.contains('is-open');
            items.forEach((groupItem) => {
              if (groupItem === item) return;
              this.setItemState(groupItem, false);
            });

            this.setItemState(item, !isOpen);

            if (!isOpen) {
              window.requestAnimationFrame(() => {
                this.setupSliders();

                const slider = item.querySelector('[data-alt-slider]');
                const swiper = slider ? this.swipers.get(slider) : null;
                swiper?.update();
              });
            }
          });
        });
      });
    }

    bindModal() {
      this.modalTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.openModal(trigger));
      });

      this.modalCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });
    }

    activateTab(index, options = {}) {
      const { preserveOpenItem = false } = options;

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.groups.forEach((group, groupIndex) => {
        const active = groupIndex === index;
        group.classList.toggle('is-active', active);

        if (this.mobileMq.matches) {
          group.hidden = !active;
        }

        if (active && this.mobileMq.matches) {
          const items = Array.from(group.querySelectorAll('[data-alt-item]'));
          const openItemIndex = preserveOpenItem ? this.getOpenItemIndex(items) : -1;
          const targetIndex = openItemIndex >= 0 ? openItemIndex : 0;
          items.forEach((item, itemIndex) => this.setItemState(item, itemIndex === targetIndex, false, false));
        }
      });

      this.setupSliders();
    }

    updateGroups() {
      if (this.mobileMq.matches) {
        const activeIndex = this.tabs.findIndex((tab) => tab.classList.contains('is-active'));
        const normalizedIndex = activeIndex >= 0 ? activeIndex : 0;
        this.activateTab(normalizedIndex, { preserveOpenItem: true });
        return;
      }

      this.groups.forEach((group) => {
        group.hidden = false;
        Array.from(group.querySelectorAll('[data-alt-item]')).forEach((item) => this.setItemState(item, true, true, false));
      });
    }

    getOpenItemIndex(items) {
      return items.findIndex((item) => item.classList.contains('is-open'));
    }

    setItemState(item, open, forceDesktop = false, animate = true) {
      const summary = item.querySelector('[data-alt-summary]');
      const content = item.querySelector('[data-alt-content]');
      if (!summary || !content) return;

      const shouldOpen = forceDesktop ? true : open;
      item.classList.toggle('is-open', shouldOpen);
      summary.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

      if (this.mobileMq.matches && !forceDesktop) {
        if (!animate) {
          window.clearTimeout(content.__altAccordionTimer);
          content.hidden = !shouldOpen;
          content.classList.toggle('is-open', shouldOpen);
          content.style.removeProperty('display');
          content.style.removeProperty('height');
          content.style.removeProperty('overflow');
          content.style.removeProperty('transition');
          content.style.removeProperty('opacity');
          content.style.removeProperty('transform');
        } else if (shouldOpen) {
          this.openItemContent(content);
        } else {
          this.closeItemContent(content);
        }
      } else {
        content.style.removeProperty('height');
        content.style.removeProperty('overflow');
        content.style.removeProperty('transition');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
        content.classList.add('is-open');
        content.hidden = false;
      }
    }

    openItemContent(content) {
      window.clearTimeout(content.__altAccordionTimer);
      content.hidden = false;
      content.classList.add('is-open');
      content.style.display = 'block';
      content.style.overflow = 'hidden';
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.transform = 'translateY(12px)';

      window.requestAnimationFrame(() => {
        content.style.transition = [
          `height ${this.accordionAnimationDuration}ms ${this.accordionAnimationEasing}`,
          `opacity 260ms ease`,
          `transform ${this.accordionAnimationDuration}ms ${this.accordionAnimationEasing}`
        ].join(', ');
        content.style.height = `${content.scrollHeight}px`;
        content.style.opacity = '1';
        content.style.transform = 'translateY(0)';
      });

      content.__altAccordionTimer = window.setTimeout(() => {
        content.style.removeProperty('height');
        content.style.removeProperty('overflow');
        content.style.removeProperty('transition');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
      }, this.accordionAnimationDuration);
    }

    closeItemContent(content) {
      window.clearTimeout(content.__altAccordionTimer);

      if (content.hidden) {
        content.style.removeProperty('height');
        content.style.removeProperty('overflow');
        content.style.removeProperty('transition');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
        content.classList.remove('is-open');
        return;
      }

      content.style.display = 'block';
      content.style.overflow = 'hidden';
      content.style.height = `${content.scrollHeight}px`;
      content.style.opacity = '1';
      content.style.transform = 'translateY(0)';

      window.requestAnimationFrame(() => {
        content.style.transition = [
          `height ${this.accordionAnimationDuration}ms ${this.accordionAnimationEasing}`,
          'opacity 180ms ease',
          `transform ${this.accordionAnimationDuration}ms ${this.accordionAnimationEasing}`
        ].join(', ');
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.transform = 'translateY(-8px)';
      });

      content.__altAccordionTimer = window.setTimeout(() => {
        content.hidden = true;
        content.classList.remove('is-open');
        content.style.removeProperty('display');
        content.style.removeProperty('height');
        content.style.removeProperty('overflow');
        content.style.removeProperty('transition');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
      }, this.accordionAnimationDuration);
    }

    setupSliders() {
      if (typeof window.Swiper !== 'function') return;

      this.sliderElements.forEach((slider) => {
        const existing = this.swipers.get(slider);
        const sectionItem = slider.closest('[data-alt-item]');
        const group = slider.closest('[data-alt-group]');
        const prev = sectionItem?.querySelector('[data-alt-prev]');
        const next = sectionItem?.querySelector('[data-alt-next]');
        const pagination = sectionItem?.querySelector('.swiper-pagination');
        const scrollbar = sectionItem?.querySelector('.swiper-scrollbar');
        const slides = slider.querySelectorAll('.swiper-slide');
        const hiddenByMobileState = this.mobileMq.matches && sectionItem && !sectionItem.classList.contains('is-open');
        const hiddenByTabState = this.mobileMq.matches && group?.hidden;

        if (slides.length <= 1) {
          existing?.destroy(true, true);
          this.swipers.delete(slider);
          return;
        }

        if (hiddenByMobileState || hiddenByTabState) {
          existing?.update();
          return;
        }

        if (existing) {
          existing.update();
          return;
        }

        const swiper = new window.Swiper(slider, {
          slidesPerView: 'auto',
          spaceBetween: this.mobileMq.matches ? 12 : 16,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          threshold: 8,
          navigation: prev && next ? { prevEl: prev, nextEl: next } : undefined,
          mousewheel: {
            forceToAxis: true, 
            sensitivity: 1,
            releaseOnEdges: true
          },
          pagination: pagination
            ? {
                el: pagination,
                type: 'fraction',

                formatFractionCurrent: function (number) {
                  return number < 10 ? '0' + number : number;
                },

                formatFractionTotal: function (number) {
                  return number < 10 ? '0' + number : number;
                }
              }
            : undefined,
          scrollbar: scrollbar ? { el: scrollbar } : undefined,
          breakpoints: {
            0: {
              slidesPerView: 1.42,
              spaceBetween: 12
            },
            750: {
              slidesPerView: 'auto',
              spaceBetween: 12
            }
          }
        });

        this.swipers.set(slider, swiper);
      });
    }

    handleResize() {
      this.setup();
    }

    openModal(trigger) {
      if (!this.modal) return;

      window.clearTimeout(this.modalAnimationTimer);
      this.activeTrigger = trigger;
      this.modalCode.textContent = trigger.dataset.modalCode || '';
      this.modalValue.textContent = trigger.dataset.modalValue || '';
      this.modalTitle.textContent = trigger.dataset.modalTitle || '';
      this.modalSubtext.textContent = trigger.dataset.modalSubtext || '';
      const modalContentId = trigger.dataset.modalContentId || '';
      const modalContent = modalContentId ? document.getElementById(modalContentId) : null;
      this.modalDescription.innerHTML = modalContent ? modalContent.innerHTML : '';
      this.modal.style.setProperty('--alt-badge-color', trigger.dataset.modalBadgeColor || '#00e51a');
      this.modal.hidden = false;
      this.modal.classList.remove('is-closing');
      document.body.classList.add('agilitas-lab-technologies-modal-open');
      document.addEventListener('keydown', this.keydownHandler);

      window.requestAnimationFrame(() => {
        this.modal?.classList.add('is-visible');
        this.modal?.querySelector('.agilitas-lab-technologies__modal-close')?.focus();
      });
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      window.clearTimeout(this.modalAnimationTimer);
      this.modal.classList.remove('is-visible');
      this.modal.classList.add('is-closing');
      document.body.classList.remove('agilitas-lab-technologies-modal-open');
      document.removeEventListener('keydown', this.keydownHandler);

      this.modalAnimationTimer = window.setTimeout(() => {
        if (!this.modal) return;
        this.modal.hidden = true;
        this.modal.classList.remove('is-closing');
      }, 320);

      this.activeTrigger?.focus();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    }

    destroy() {
      clearTimeout(this.retryTimer);
      window.clearTimeout(this.modalAnimationTimer);
      this.closeModal();
      window.removeEventListener('resize', this.resizeHandler);

      if (this.mobileMq.removeEventListener) {
        this.mobileMq.removeEventListener('change', this.mediaChangeHandler);
      } else {
        this.mobileMq.removeListener(this.mediaChangeHandler);
      }

      this.sliderElements.forEach((slider) => {
        const swiper = this.swipers.get(slider);
        swiper?.destroy(true, true);
      });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-agilitas-lab-technologies]').forEach((section) => {
      if (section.__agilitasLabTechnologies) return;
      section.__agilitasLabTechnologies = new AgilitasLabTechnologies(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
